/* eslint-disable react/display-name, react/prop-types */

import React, { useState, useLayoutEffect } from 'react';

import AuthenticatedContainer from 'components/AuthenticatedContainer';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TermsOfUseContent from '../Components/TermsOfUseContent';
import { PAGE_TOP_PADDING } from '../Components/paginatePolicy';
import usePolicyPagination from '../Components/usePolicyPagination';

export const TermsOfUse = () => {
  const {
    outerRef,
    measureRef,
    displayRef,
    footerRef,
    navigationRef,
    totalPages,
    currentPage,
    setCurrentPage,
    isLastPage,
  } = usePolicyPagination();
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const bottomNavigation = document.getElementById('app-bottom-navigation');
    const header = document.querySelector('.nav-container');
    const viewport = window.visualViewport;
    const updateHeight = () => {
      // Document scrolling must not enlarge the panel and repaginate the terms.
      const contentTop = outer.getBoundingClientRect().top + window.scrollY;
      const navigationHeight = bottomNavigation?.getBoundingClientRect().height || 0;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      setHeight(Math.max(0, viewportHeight - contentTop - navigationHeight));
    };
    const observer = new ResizeObserver(updateHeight);
    if (bottomNavigation) observer.observe(bottomNavigation);
    if (header) observer.observe(header);
    window.addEventListener('resize', updateHeight);
    viewport?.addEventListener('resize', updateHeight);
    updateHeight();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
      viewport?.removeEventListener('resize', updateHeight);
    };
  }, [outerRef]);

  return (
    <AuthenticatedContainer>
      <Box
        ref={outerRef}
        sx={{
          width: '100%',
          height,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Hidden measurement container */}
        <Box
          ref={measureRef}
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            top: 0,
            left: 0,
            right: 0,
            height: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        >
          <TermsOfUseContent />
        </Box>

        {/* Visible display */}
        <Box
          ref={displayRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            pt: `${PAGE_TOP_PADDING}px`,
          }}
        >
          <TermsOfUseContent />
        </Box>

        {/* Navigation bar */}
        <Box
          ref={footerRef}
          sx={{
            flexShrink: 0,
            bgcolor: 'background.paper',
            pb: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <Box
            ref={navigationRef}
            sx={{
              px: 2,
              py: 1,
              borderTop: '1px solid #e0e0e0',
              '& .MuiButton-root': { minHeight: 44 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 44,
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                {currentPage > 0 && (
                  <Button onClick={() => setCurrentPage((p) => p - 1)}>Back</Button>
                )}
              </Box>

              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                {totalPages > 1 && (
                  <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
                    Page {currentPage + 1} of {totalPages}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {totalPages > 0 && !isLastPage && (
                  <Button onClick={() => setCurrentPage((p) => p + 1)}>{'Next ->'}</Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </AuthenticatedContainer>
  );
};

export default TermsOfUse;
